import React, { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ent, setEnt] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (user) api.get("/entitlement").then((r) => setEnt(r.data)).catch((e) => console.error("entitlement fetch failed", e));
  }, [user]);

  const checkout = async (lookup_key) => {
    if (!user) { navigate("/login"); return; }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout",
        { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout error");
      setBusy("");
    }
  };

  const plans = [
    { key: "credits_1", name: t("pr_single_name"), price: t("pr_single_price"), per: "",
      feats: [t("pr_single_f1"), t("pr_single_f2"), t("pr_single_f3")], cta: t("pr_single_cta"), icon: Check },
    { key: "starter_monthly", name: "Starter", price: "€49", per: t("pr_month"),
      feats: [t("pr_starter_f1"), t("pr_starter_f2"), t("pr_starter_f3")], cta: t("pr_cta_sub"), icon: Zap },
    { key: "professional_monthly", name: "Professional", price: "€149", per: t("pr_month"), popular: true,
      feats: [t("pr_pro_f1"), t("pr_pro_f2"), t("pr_pro_f3"), t("pr_pro_f4")], cta: t("pr_cta_sub"), icon: Sparkles },
    { key: "credits_10", name: t("pr_credit_name"), price: t("pr_credit_price"), per: "",
      feats: [t("pr_credit_f1"), t("pr_credit_f2"), t("pr_credit_f3")], cta: t("pr_cta_buy"), icon: Check },
  ];

  return (
    <div className="App bg-slate-900 min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">{t("pr_title")}</h1>
          <p className="mt-4 text-slate-300">{t("pr_sub")}</p>
          {ent && !ent.plan_active && ent.credits > 0 && (
            <p className="mt-3 inline-block rounded-full bg-blue-950 border border-blue-800 text-blue-300 text-sm font-medium px-4 py-1.5" data-testid="credits-left">
              {ent.credits} {t("ent_credits_badge")}
            </p>
          )}
          {ent && ent.plan_active && (
            <p className="mt-3 inline-block rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold px-4 py-1.5" data-testid="active-plan">
              {ent.plan.toUpperCase()} · attivo
            </p>
          )}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {plans.map((p) => (
            <div key={p.key} data-testid={`plan-${p.key}`}
              className={`relative rounded-2xl border bg-slate-900 p-7 transition-[transform,box-shadow] duration-200 hover:shadow-xl ${p.popular ? "border-blue-500 ring-2 ring-blue-200 md:-mt-3 md:mb-3" : "border-slate-800"}`}>
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 text-white text-[11px] font-semibold px-3 py-1">{t("pr_popular")}</span>
              )}
              <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${p.popular ? "bg-blue-600 text-white" : "bg-slate-900 text-white"}`}>
                <p.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-heading text-xl font-bold text-white">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-extrabold text-white">{p.price}</span>
                <span className="text-slate-400 text-sm">{p.per}</span>
              </div>
              <ul className="mt-6 space-y-3">
                {p.feats.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <Button className="mt-7 w-full h-11" variant={p.popular ? "default" : "outline"}
                data-testid={`buy-${p.key}`} disabled={busy === p.key} onClick={() => checkout(p.key)}>
                {busy === p.key ? t("pr_loading") : p.cta}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-500 mt-10">Test: carta 4242 4242 4242 4242 · scadenza futura · qualsiasi CVC</p>
      </main>
    </div>
  );
}
